import { useState, useEffect, useRef } from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuCheckboxItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { ChevronDown, Filter, Building2 } from 'lucide-react';
import { useLanguage } from '@/contexts/LanguageContext';
import { collection, getDocs } from 'firebase/firestore';
import { db } from '@/lib/firebase/config';
import type { Organization } from '@/types/organization';

interface UserOrganizationFiltersProps {
  selected: string[];
  onSelect: (organizationIds: string[]) => void;
  title?: string;
}

export function UserOrganizationFilters({ selected, onSelect, title }: UserOrganizationFiltersProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [loading, setLoading] = useState(true);
  const { t, dir } = useLanguage();
  const fetchedRef = useRef(false);

  useEffect(() => {
    const fetchOrganizations = async () => {
      // Prevent duplicate fetches (React Strict Mode causes double renders in dev)
      if (fetchedRef.current) return;
      fetchedRef.current = true;
      
      try {
        setLoading(true);
        const orgsQuery = collection(db, 'organizations');
        const snapshot = await getDocs(orgsQuery);
        
        const orgs: Organization[] = [];
        snapshot.forEach(doc => {
          const orgData = { id: doc.id, ...doc.data() } as Organization;
          orgs.push(orgData);
        });
        setOrganizations(orgs);
      } catch (error) {
        console.error('❌ Error fetching organizations:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchOrganizations();
  }, []);

  const toggleOrganization = (orgId: string) => {
    if (selected.includes(orgId)) {
      onSelect(selected.filter(id => id !== orgId));
    } else {
      onSelect([...selected, orgId]);
    }
  };

  const clearAll = () => {
    onSelect([]);
  };

  const selectAll = () => {
    onSelect(organizations.map(org => org.id));
  };

  const getSelectedOrganizations = () => {
    return organizations.filter(org => selected.includes(org.id));
  };

  const getDisplayText = () => {
    if (selected.length === 0) {
      return t('dashboard.admin.users.filters.allOrganizations');
    } else if (selected.length === 1) {
      const org = organizations.find(o => o.id === selected[0]);
      return org ? (dir === 'rtl' ? (org.nameAr || org.name) : (org.nameEn || org.name)) : selected[0];
    } else {
      return `${selected.length} ${t('dashboard.admin.users.filters.selected')}`;
    }
  };

  return (
    <div className="flex flex-col gap-2">
      <DropdownMenu open={isOpen} onOpenChange={setIsOpen}>
        <DropdownMenuTrigger asChild>
          <Button variant="outline" className={`flex items-center gap-2 ${dir === 'rtl' ? 'flex-row-reverse' : 'flex-row'}`}>
            <Building2 className="h-4 w-4" />
            {loading ? (
              t('dashboard.admin.users.filters.loading')
            ) : (
              getDisplayText()
            )}
            <ChevronDown className="h-4 w-4" />
          </Button>
        </DropdownMenuTrigger>
        
        <DropdownMenuContent className="w-56" align={dir === 'rtl' ? 'end' : 'start'}>
          <DropdownMenuLabel>{title || t('dashboard.admin.users.filters.selectOrganizations')}</DropdownMenuLabel>
          <DropdownMenuSeparator />
          
          <DropdownMenuCheckboxItem
            checked={selected.length === organizations.length && organizations.length > 0}
            onCheckedChange={selectAll}
            disabled={loading}
          >
            {t('dashboard.admin.users.filters.selectAll')}
          </DropdownMenuCheckboxItem>
          
          <DropdownMenuCheckboxItem
            checked={selected.length === 0}
            onCheckedChange={clearAll}
            disabled={loading}
          >
            {t('dashboard.admin.users.filters.clearAll')}
          </DropdownMenuCheckboxItem>
          
          <DropdownMenuSeparator />
          
          {loading ? (
            <div className="p-2 text-center text-sm text-muted-foreground">
              {t('dashboard.admin.users.filters.loading')}
            </div>
          ) : organizations.length === 0 ? (
            <div className="p-2 text-center text-sm text-muted-foreground">
              {t('dashboard.admin.users.filters.noOrganizations')}
            </div>
          ) : (
            organizations.map((org) => (
                             <DropdownMenuCheckboxItem
                 key={org.id}
                 checked={selected.includes(org.id)}
                 onCheckedChange={() => toggleOrganization(org.id)}
               >
                 {dir === 'rtl' ? (org.nameAr || org.name) : (org.nameEn || org.name)}
               </DropdownMenuCheckboxItem>
            ))
          )}
        </DropdownMenuContent>
      </DropdownMenu>

      
    </div>
  );
} 